import { DownloadSharp } from "@mui/icons-material";
import { Box, Button, Paper, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import type { ChangeEvent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";

import CustomConfirmDialog from "../../components/CustomConfirmDialog";
import { ActionButtonsGroup } from "../../components/crm/OrderActionButtons";
import OrderCsvExportDialog from "../../components/crm/OrderCsvExportDialog";
import {
  DEFAULT_STATUSES,
  FilterControls,
  type OrderListFilters,
} from "../../components/crm/OrderFilterControls";
import OrderStatusUpdateDialog from "../../components/crm/OrderStatusUpdateDialog";
import OrderRowActionMenu from "../../components/crm/OrderRowActionMenu";
import OrderTable from "../../components/crm/OrderTable";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import useCrmOrders from "../../hooks/useCrmOrders";
import {
  deleteAdminOrder,
  getAdminOrder,
  updateAdminOrder,
} from "../../services/order.service";
import type { Order, OrderFilters, OrderStatus } from "../../types/order";
import { downloadOrderXml } from "../../utils/orderXmlBuilder";

export default function CrmOrders() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const currentYear = dayjs().year();

  type StatusDialogState = {
    open: boolean;
    orderIds: string[];
    status: OrderStatus;
    trackingNumbers: { id: string; value: string }[];
    trackingDate: string;
    details: string;
    title: string;
    subtitle: string;
    submitLabel: string;
    cancelLabel: string;
  };

  type State = {
    filters: OrderListFilters;
    statusDialog: StatusDialogState;
    deleteDialogOpen: boolean;
    selectedOrder: Order | null;
    isSubmitting: boolean;
    selectedIds: string[];
    actionAnchorEl: HTMLElement | null;
    actionRow: Order | null;
  };

  type Action =
    | { type: "SET_FILTERS"; payload: Partial<OrderListFilters> }
    | { type: "SET_STATUS_DIALOG"; payload: StatusDialogState }
    | { type: "SET_STATUS_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_STATUS_DIALOG_STATUS"; payload: OrderStatus }
    | { type: "SET_STATUS_DIALOG_TRACKING"; payload: { id: string; value: string }[] }
    | { type: "SET_STATUS_DIALOG_TRACKING_DATE"; payload: string }
    | { type: "SET_STATUS_DIALOG_DETAILS"; payload: string }
    | { type: "SET_DELETE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_SELECTED_ORDER"; payload: Order | null }
    | { type: "SET_IS_SUBMITTING"; payload: boolean }
    | { type: "SET_SELECTED_IDS"; payload: string[] }
    | { type: "SET_ACTION_ANCHOR_EL"; payload: HTMLElement | null }
    | { type: "SET_ACTION_ROW"; payload: Order | null }
    | {
        type: "OPEN_ACTION_MENU";
        payload: { anchorEl: HTMLElement; row: Order };
      }
    | { type: "CLOSE_DIALOGS" };

  const defaultStatusDialog: StatusDialogState = {
    open: false,
    orderIds: [],
    status: "RECEIVED",
    trackingNumbers: [
      { id: Math.random().toString(36).substring(2, 9), value: "" },
    ],
    trackingDate: "",
    details: "",
    title: "",
    subtitle: "",
    submitLabel: "Update Status",
    cancelLabel: "Discard",
  };

  const initialState: State = {
    filters: {
      search: "",
      statuses: DEFAULT_STATUSES,
      month: "all",
      year: String(currentYear),
      page: 0,
      page_size: 10,
    },
    statusDialog: defaultStatusDialog,
    deleteDialogOpen: false,
    selectedOrder: null,
    isSubmitting: false,
    selectedIds: [],
    actionAnchorEl: null,
    actionRow: null,
  };

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case "SET_FILTERS":
        return {
          ...state,
          filters: { ...state.filters, ...action.payload },
          selectedIds: [],
        };
      case "SET_STATUS_DIALOG":
        return { ...state, statusDialog: action.payload };
      case "SET_STATUS_DIALOG_OPEN":
        return {
          ...state,
          statusDialog: { ...state.statusDialog, open: action.payload },
        };
      case "SET_STATUS_DIALOG_STATUS":
        return {
          ...state,
          statusDialog: { ...state.statusDialog, status: action.payload },
        };
      case "SET_STATUS_DIALOG_TRACKING":
        return {
          ...state,
          statusDialog: { ...state.statusDialog, trackingNumbers: action.payload },
        };
      case "SET_STATUS_DIALOG_TRACKING_DATE":
        return {
          ...state,
          statusDialog: { ...state.statusDialog, trackingDate: action.payload },
        };
      case "SET_STATUS_DIALOG_DETAILS":
        return {
          ...state,
          statusDialog: { ...state.statusDialog, details: action.payload },
        };
      case "SET_DELETE_DIALOG_OPEN":
        return { ...state, deleteDialogOpen: action.payload };
      case "SET_SELECTED_ORDER":
        return { ...state, selectedOrder: action.payload };
      case "SET_IS_SUBMITTING":
        return { ...state, isSubmitting: action.payload };
      case "SET_SELECTED_IDS":
        return { ...state, selectedIds: action.payload };
      case "SET_ACTION_ANCHOR_EL":
        return { ...state, actionAnchorEl: action.payload };
      case "SET_ACTION_ROW":
        return { ...state, actionRow: action.payload };
      case "OPEN_ACTION_MENU":
        return {
          ...state,
          actionAnchorEl: action.payload.anchorEl,
          actionRow: action.payload.row,
        };
      case "CLOSE_DIALOGS":
        return {
          ...state,
          statusDialog: { ...state.statusDialog, open: false },
          deleteDialogOpen: false,
        };
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const {
    filters,
    statusDialog,
    deleteDialogOpen,
    selectedOrder,
    isSubmitting,
    selectedIds,
    actionAnchorEl,
    actionRow,
  } = state;

  useEffect(() => {
    setBreadcrumbs([{ label: "Order List", to: "/crm/orders" }]);
  }, [setBreadcrumbs]);

  const yearOptions = useMemo(
    () => [
      { label: "All Year", value: "all" },
      ...Array.from({ length: 8 }, (_, index) => {
        const year = String(currentYear - index);
        return { label: year, value: year };
      }),
    ],
    [currentYear],
  );

  const apiFilters = useMemo<OrderFilters>(() => {
    const hasSpecificYear = filters.year !== "all";
    const hasSpecificMonth = filters.month !== "all";
    const selectedYear = Number(filters.year);
    const selectedMonth = Number(filters.month) - 1;

    const timeRange =
      hasSpecificYear && Number.isFinite(selectedYear)
        ? {
            time_from: hasSpecificMonth
              ? dayjs()
                  .year(selectedYear)
                  .month(selectedMonth)
                  .startOf("month")
                  .toISOString()
              : dayjs().year(selectedYear).startOf("year").toISOString(),
            time_to: hasSpecificMonth
              ? dayjs()
                  .year(selectedYear)
                  .month(selectedMonth)
                  .endOf("month")
                  .toISOString()
              : dayjs().year(selectedYear).endOf("year").toISOString(),
          }
        : {};

    return {
      search: filters.search || undefined,
      page: filters.page + 1,
      page_size: filters.page_size,
      ...(filters.statuses.length > 0
        ? { statuses: filters.statuses }
        : {}),
      sort: ["created_at|1"],
      ...timeRange,
    };
  }, [filters]);

  const { data, dataLoading, mutate } = useCrmOrders(true, apiFilters);

  const orders = useMemo<Order[]>(() => data?.data ?? [], [data]);
  const totalRecords = data?.total_items || 0;
  const isLoading = dataLoading;

  const handleSearchChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { search: value, page: 0 } });
  }, []);

  const handleStatusesChange = useCallback((value: OrderStatus[]) => {
    dispatch({
      type: "SET_FILTERS",
      payload: {
        statuses: value,
        page: 0,
      },
    });
  }, []);

  const handleMonthChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { month: value, page: 0 } });
  }, []);

  const handleYearChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { year: value, page: 0 } });
  }, []);

  const handlePageChange = useCallback((_: unknown, page: number) => {
    dispatch({ type: "SET_FILTERS", payload: { page } });
  }, []);

  const handlePageSizeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: "SET_FILTERS",
        payload: {
          page_size: parseInt(event.target.value, 10),
          page: 0,
        },
      });
    },
    [],
  );

  const handleView = (order: Order) => {
    if (!order.id) return;
    navigate(`/crm/orders/${order.id}/edit`);
  };

  const openApproveDialog = (orderIds: string[]) => {
    if (!orderIds.length) return;
    dispatch({
      type: "SET_STATUS_DIALOG",
      payload: {
        open: true,
        orderIds,
        status: "CREATED",
        trackingNumbers: [
          { id: Math.random().toString(36).substring(2, 9), value: "" },
        ],
        trackingDate: "",
        details: "",
        title: `Order Created (${orderIds.length})`,
        subtitle: "Are you sure you want to update this Order?",
        submitLabel: "Update Status",
        cancelLabel: "Discard",
      },
    });
  };

  const handleApproveSingle = (order: Order) => {
    if (!order.id) return;
    dispatch({ type: "SET_SELECTED_ORDER", payload: order });
    openApproveDialog([order.id]);
  };

  const handleApproveBulk = () => {
    openApproveDialog(selectedIds);
  };

  const openRequestDialog = (orderIds: string[]) => {
    if (!orderIds.length) return;
    dispatch({
      type: "SET_STATUS_DIALOG",
      payload: {
        open: true,
        orderIds,
        status: "REQUEST_MORE_INFO",
        trackingNumbers: [
          { id: Math.random().toString(36).substring(2, 9), value: "" },
        ],
        trackingDate: "",
        details: "",
        title: "Update State Order",
        subtitle: "Please review carefully before updating the status.",
        submitLabel: "Update Status",
        cancelLabel: "Discard",
      },
    });
  };

  const handleRequestSingle = (order: Order) => {
    if (!order.id) return;
    dispatch({ type: "SET_SELECTED_ORDER", payload: order });
    openRequestDialog([order.id]);
  };

  const handleRequestBulk = () => {
    openRequestDialog(selectedIds);
  };

  const handleDeleteClick = (order: Order) => {
    dispatch({ type: "SET_SELECTED_ORDER", payload: order });
    dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: true });
  };

  const handleBulkDelete = () => {
    if (!selectedIds.length) return;
    dispatch({ type: "SET_SELECTED_ORDER", payload: null });
    dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: true });
  };

  const handleBulkCancel = () => {
    if (!selectedIds.length) return;
    dispatch({
      type: "SET_STATUS_DIALOG",
      payload: {
        open: true,
        orderIds: selectedIds,
        status: "CANCELLED",
        trackingNumbers: [
          { id: Math.random().toString(36).substring(2, 9), value: "" },
        ],
        trackingDate: "",
        details: "",
        title: `Cancel Orders (${selectedIds.length})`,
        subtitle: "Are you sure you want to cancel? Please provide a reason.",
        submitLabel: "Cancel Order",
        cancelLabel: "Discard",
      },
    });
  };

  const handleStatusDialogSubmit = async () => {
    if (!statusDialog.orderIds.length) return;
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });

    const isCreated = statusDialog.status === "CREATED";
    const trackingNumbers = statusDialog.trackingNumbers
      .map((t) => t.value.trim())
      .filter(Boolean);

    try {
      await Promise.all(
        statusDialog.orderIds.map((orderId: string) =>
          updateAdminOrder(orderId, {
            status: statusDialog.status,
            ...(isCreated
              ? {
                  tracking_number: trackingNumbers.length ? trackingNumbers : undefined,
                  tracking_date: statusDialog.trackingDate
                    ? `${statusDialog.trackingDate}T00:00:00Z`
                    : undefined,
                  remarks: statusDialog.details.trim() || undefined,
                }
              : { remarks: statusDialog.details.trim() }),
          }),
        ),
      );

      dispatch({ type: "SET_STATUS_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
      mutate();
      showSnackbar("Order(s) updated successfully", "success");
    } catch (error) {
      console.error("Error updating orders:", error);
      showSnackbar("Failed to update order(s)", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleDelete = async () => {
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      if (selectedOrder?.id) {
        await deleteAdminOrder(selectedOrder.id);
      } else if (selectedIds.length > 0) {
        await Promise.all(
          selectedIds.map((orderId: string) => deleteAdminOrder(orderId)),
        );
      } else {
        return;
      }
      dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_ORDER", payload: null });
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
      mutate();
      showSnackbar(
        selectedOrder?.id
          ? "Order deleted successfully"
          : "Order(s) deleted successfully",
        "success",
      );
    } catch (error) {
      console.error("Error deleting order:", error);
      showSnackbar("Failed to delete order(s)", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };


  const selectedStatuses = useMemo(
    () =>
      orders
        .filter((o) => o.id && selectedIds.includes(o.id))
        .map((o) => o.status)
        .filter((s): s is OrderStatus => Boolean(s)),
    [orders, selectedIds],
  );

  const visibleOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateA - dateB;
    });
  }, [orders]);

  const allSelected =
    visibleOrders.length > 0 &&
    visibleOrders.every((row) => row.id && selectedIds.includes(row.id));
  const isIndeterminate =
    selectedIds.length > 0 && !allSelected && visibleOrders.length > 0;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      dispatch({
        type: "SET_SELECTED_IDS",
        payload: visibleOrders.map((row) => row.id || "").filter(Boolean),
      });
    } else {
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
    }
  };

  const handleSelectRow = (id: string) => {
    dispatch({
      type: "SET_SELECTED_IDS",
      payload: selectedIds.includes(id)
        ? selectedIds.filter((rowId: string) => rowId !== id)
        : [...selectedIds, id],
    });
  };

  const handleOpenActionMenu = (
    order: Order,
    event: MouseEvent<HTMLElement>,
  ) => {
    dispatch({
      type: "OPEN_ACTION_MENU",
      payload: { anchorEl: event.currentTarget, row: order },
    });
  };

  const handleCloseActionMenu = () => {
    dispatch({ type: "SET_ACTION_ANCHOR_EL", payload: null });
  };

  const handleActionMenuExited = () => {
    dispatch({ type: "SET_ACTION_ROW", payload: null });
  };

  const handleActionView = () => {
    if (actionRow) handleView(actionRow);
    handleCloseActionMenu();
  };

  const handleActionApprove = () => {
    if (actionRow) handleApproveSingle(actionRow);
    handleCloseActionMenu();
  };

  const handleActionRequestMoreDetails = () => {
    if (actionRow) handleRequestSingle(actionRow);
    handleCloseActionMenu();
  };

  const handleActionDelete = () => {
    if (actionRow) handleDeleteClick(actionRow);
    handleCloseActionMenu();
  };
  const actionRowStatus = actionRow?.status;

  const handleDownloadXml = async () => {
    if (!selectedIds.length) return;
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      const results = await Promise.all(
        selectedIds.map((id) => getAdminOrder(id)),
      );
      for (const order of results) {
        if (order) await downloadOrderXml(order);
      }
      showSnackbar("XML downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading XML:", error);
      showSnackbar("Failed to download XML", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexWrap: { xs: "wrap", md: "nowrap" },
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <FilterControls
            filters={filters}
            yearOptions={yearOptions}
            onSearchChange={handleSearchChange}
            onMonthChange={handleMonthChange}
            onYearChange={handleYearChange}
            onStatusesChange={handleStatusesChange}
          />
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              type="button"
              variant="outlined"
              startIcon={<DownloadSharp />}
              onClick={() => setExportDialogOpen(true)}
            >
              Export
            </Button>
            <ActionButtonsGroup
              selectedIds={selectedIds}
              selectedStatuses={selectedStatuses}
              onDelete={handleBulkDelete}
              onCancel={handleBulkCancel}
              onRequestBulk={handleRequestBulk}
              onDownloadXml={handleDownloadXml}
              onApproveBulk={handleApproveBulk}
            />
          </Stack>
        </Box>

        <OrderTable
          visibleOrders={visibleOrders}
          isLoading={isLoading}
          filters={filters}
          totalRecords={totalRecords}
          selectedIds={selectedIds}
          allSelected={allSelected}
          isIndeterminate={isIndeterminate}
          onSelectAll={handleSelectAll}
          onSelectRow={handleSelectRow}
          onOpenActionMenu={handleOpenActionMenu}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      </Paper>

      <OrderCsvExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        baseFilters={{
          search: apiFilters.search,
          statuses: apiFilters.statuses,
          sort: apiFilters.sort,
        }}
      />

      <OrderRowActionMenu
        anchorEl={actionAnchorEl}
        status={actionRowStatus}
        onClose={handleCloseActionMenu}
        onExited={handleActionMenuExited}
        onView={handleActionView}
        onApprove={handleActionApprove}
        onRequestMoreDetails={handleActionRequestMoreDetails}
        onDelete={handleActionDelete}
      />

      <OrderStatusUpdateDialog
        open={statusDialog.open}
        title={statusDialog.title}
        subtitle={statusDialog.subtitle}
        status={statusDialog.status}
        trackingNumbers={statusDialog.trackingNumbers}
        trackingDate={statusDialog.trackingDate}
        details={statusDialog.details}
        isSubmitting={isSubmitting}
        submitLabel={statusDialog.submitLabel}
        cancelLabel={statusDialog.cancelLabel}
        onClose={() => dispatch({ type: "SET_STATUS_DIALOG_OPEN", payload: false })}
        onStatusChange={(status) =>
          dispatch({ type: "SET_STATUS_DIALOG_STATUS", payload: status })
        }
        onTrackingNumbersChange={(trackingNumbers) =>
          dispatch({ type: "SET_STATUS_DIALOG_TRACKING", payload: trackingNumbers })
        }
        onTrackingDateChange={(trackingDate) =>
          dispatch({ type: "SET_STATUS_DIALOG_TRACKING_DATE", payload: trackingDate })
        }
        onDetailsChange={(details) =>
          dispatch({ type: "SET_STATUS_DIALOG_DETAILS", payload: details })
        }
        onSubmit={handleStatusDialogSubmit}
      />

      <CustomConfirmDialog
        open={deleteDialogOpen}
        title={
          selectedOrder
            ? "Delete Order"
            : `Delete ${selectedIds.length} Order(s)`
        }
        onClose={() =>
          dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false })
        }
        content={
          <Typography>
            {selectedOrder
              ? "Are you sure you want to delete this order? This action cannot be undone."
              : `Are you sure you want to delete ${selectedIds.length} selected order(s)? This action cannot be undone.`}
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false })
              }
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={
                isSubmitting ||
                (!selectedOrder?.id && selectedIds.length === 0)
              }
            >
              Delete
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
}
